import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getDashboardUnknownGuests, getDashboardUnknownGuestsSelector} from '@/store/organization-dashboard';
//mui
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel} from '@mui/material';
//constants
import {
	BID,
	BIDDER_ID,
	DOWNLOAD_CSV,
	EMAIL,
	FIRST_NAME_CAMEL,
	HASH,
	NAME,
	PHONE_NUMBER,
	SEAT_TYPE,
	TABLE,
	TABLE_NAME,
	TABLE_NAME_CAMEL,
	TABLE_NUMBER_CAMEL,
	defaultPage,
	defaultPageSize,
} from '@/utils/constants';
//helpers
import {downloadFile, getErrorMessage} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
//services
import {OrganizationDashboardService} from '@/utils/services';
//enums
import {STATISTIC, TABLES_ENTITIES} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticUnknownGuestsStyle} from './StatisticUnknownGuests.styles';

const StatisticUnknownGuests = () => {
	const dispatch = useAppDispatch();

	const [currentPage, setCurrentPage] = useState(defaultPage);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [searchName, setSearchName] = useState('');

	const [sortColumn, setSortColumn] = useState<string>();
	const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const unknownGuests = useAppSelector(getDashboardUnknownGuestsSelector);

	const {data, pageSize, totalCount} = unknownGuests;

	useEffect(() => {
		if (currentEvent) getUnknownGuests(searchName);
	}, [currentEvent, currentPage, sortColumn, isOrderDesc]);

	const handleSortChange = (newSortColumn: string) => () => {
		const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
		setIsOrderDesc(isAsc);
		setSortColumn(newSortColumn);
	};

	const getUnknownGuests = async (value?: string) => {
		setIsDataLoaded(false);

		try {
			const reqBody = {
				eventId: currentEvent!.id,
				page: currentPage,
				sortColumn,
				isOrderDesc,
				searchName: value,
			};

			await dispatch(getDashboardUnknownGuests(reqBody)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsDataLoaded(true);
		}
	};

	const handleDownloadSvc = async () => {
		if (currentEvent) {
			const file = await OrganizationDashboardService.getDashboardUnknownGuestsCsv(currentEvent.id);
			downloadFile(file.data, STATISTIC.UNKNOWN_GUESTS);
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchName(value);
		getUnknownGuests(value);
	};

	const handlePaginationChange = (step: number) => {
		if (isOrderDesc) setIsOrderDesc(false);
		if (sortColumn?.length) setSortColumn('');

		setCurrentPage(step);
	};

	const handlePressEnter = () => {
		setCurrentPage(defaultPage);
		if (currentEvent) getUnknownGuests(searchName);
	};

	return (
		<Box sx={statisticUnknownGuestsStyle.wrapper}>
			<SearchHeader
				page={currentPage}
				items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
				total={totalCount}
				onPressEnter={handlePressEnter}
				onSearchChange={handleSearchChange}
				unitName={TABLES_ENTITIES.GUESTS}
				actionButton={
					<Button
						onClick={handleDownloadSvc}
						title={DOWNLOAD_CSV}
						disabled={!totalCount}
						startIcon={<ImportFileIcon />}
						sx={statisticUnknownGuestsStyle.searchButton}
					/>
				}
			/>

			{isDataLoaded ? (
				totalCount ? (
					<Box sx={statisticUnknownGuestsStyle.tableContent}>
						<TableContainer sx={statisticUnknownGuestsStyle.container}>
							<Table sx={statisticUnknownGuestsStyle.table}>
								<TableHead>
									<TableRow sx={statisticUnknownGuestsStyle.tableHeader}>
										<TableCell>
											<TableSortLabel active onClick={handleSortChange(TABLE_NUMBER_CAMEL)} IconComponent={UpDownIcon}>
												{TABLE}
												{HASH}
											</TableSortLabel>
										</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(TABLE_NAME_CAMEL)} IconComponent={UpDownIcon}>
												{TABLE_NAME}
											</TableSortLabel>
										</TableCell>

										<TableCell>{SEAT_TYPE}</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(BID)} IconComponent={UpDownIcon}>
												{BIDDER_ID}
												{HASH}
											</TableSortLabel>
										</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(FIRST_NAME_CAMEL)} IconComponent={UpDownIcon}>
												{NAME}
											</TableSortLabel>
										</TableCell>

										<TableCell>{EMAIL}</TableCell>

										<TableCell>{PHONE_NUMBER}</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{data.map((row) => (
										<TableRow key={row.tableNumber} sx={statisticUnknownGuestsStyle.tableRow}>
											<TableCell>
												{HASH}
												{row.tableNumber}
											</TableCell>
											<TableCell>{row.tableName}</TableCell>
											<TableCell>Table ({row.seatType} person)</TableCell>
											<TableCell>{row.attendeeId || '-'}</TableCell>
											<TableCell>{row.attendee}</TableCell>
											<TableCell>{row.email || '-'}</TableCell>
											<TableCell>{row.phone || '-'}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={statisticUnknownGuestsStyle.pagination}>
							<Pagination
								page={currentPage}
								pageSize={pageSize}
								total={totalCount}
								onChange={(step) => handlePaginationChange(step)}
							/>
						</Box>
					</Box>
				) : (
					<EmptyTableData />
				)
			) : (
				<Loader parentClassName={statisticUnknownGuestsStyle.loader} size={50} />
			)}
		</Box>
	);
};

export default StatisticUnknownGuests;
